import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GungNavbarService, GungShopMenu } from 'gung-standard';
import { environment } from '../../environments/environment';
import { filter, first, map, Observable, of, switchMap, tap } from 'rxjs';
import { CarLinerMysetupModalComponent } from '../components/car-liner-mysetup-modal/car-liner-mysetup-modal.component';

@Injectable({
  providedIn: 'root'
})
export class CarLinerGungNavbarService extends GungNavbarService {
  private modalService = inject(NgbModal);
  private modalRef: NgbModalRef;

  override getLogoUrlObservable(): Observable<string> {
    if (environment.projectName === 'josam') {
      return of('/assets/Josam-logo-2015-REGULAR-Modern.jpg');
    }
    if (environment.projectName === 'blackhawk') {
      return of('./assets/blackhawk_sim_1100.png');
    }
    if (environment.projectName === 'thailand') {
      return of('./assets/logo.png');
    }
    return super.getLogoUrlObservable().pipe(
      switchMap(logo =>
        logo
          ? of(logo)
          : this.authService.getCurrentUser().pipe(
              first(),
              map(user => (user?.extra?.customLogo ? 'https://cdn2.gung.io/' + user.extra.customLogo : './assets/logo.png'))
            )
      )
    );
  }

  // Forces search and "my customers" hidden regardless of what the company layout config says,
  // by tapping onto the base's own resolution instead of racing it after the fact.
  protected override applyCompanyNavbarConfig(): Observable<void> {
    return super.applyCompanyNavbarConfig().pipe(
      tap(() => {
        this.showNavbarSearch = false;
        this.hideMyCustomers = true;
      })
    );
  }

  // Replaces the flow/assortment-based shop menu with car-liner's fixed top-nav links,
  // same set as the old CarLinerNavbarConfigService.getLeftMenus().
  override getShopMenu(): Observable<GungShopMenu[]> {
    return this.selectedCustomerService.getSelectedCustomer().pipe(
      switchMap(customer =>
        this.authService.getRoles().pipe(
          filter(roles => !!roles),
          first(),
          map(roles => {
            const menu: GungShopMenu[] = [
              { link: '/products', heading: 'PRICELIST' },
              {
                heading: 'NEW_PRODUCTS',
                action: () =>
                  this.router.navigate(['products'], {
                    queryParams: { filters: 'Miscellaneous__:__SHOW_NEW_PRODUCTS' }
                  })
              },
              { link: `/customers/${customer.id}`, heading: 'MY_CUSTOMER' },
              { heading: 'MY_SETUP', action: () => this.openMySetup() },
              { heading: 'LOGOUT', action: () => this.authService.logout() }
            ];
            if (!(roles.includes('SALES') || roles.includes('DISTRIBUTOR') || roles.includes('ADMIN'))) {
              menu.splice(
                menu.findIndex(m => m.heading === 'MY_CUSTOMER'),
                1
              );
            }
            if (roles.includes('END_USER')) {
              menu.splice(
                menu.findIndex(m => m.heading === 'MY_SETUP'),
                1
              );
            }
            return menu;
          })
        )
      )
    );
  }

  private openMySetup(): void {
    this.modalRef = this.modalService.open(CarLinerMysetupModalComponent, {
      backdrop: 'static',
      size: 'lg',
      keyboard: false
    });
    this.modalRef.result.then(
      () => {},
      () => {}
    );
  }
}
